/**
 * OKS-833 — Single source of truth for the masterclass-MODULE-cover aspect
 * ratio.
 *
 * A masterclass module gets a rich card whose cover band shares the
 * mission-card geometry: **3:2**. This is DELIBERATELY distinct from the
 * masterclass cover (`lib/masterclass-cover.ts`), which is hard-locked to
 * 16:5 — a banner, not a card cover. The two must never be conflated: a
 * module surface importing `MASTERCLASS_COVER_ASPECT_CLASS` would frame the
 * card cover at 16:5 and break framed==shown. LQE's ratio-correctness guard
 * reds if any module surface imports the 16:5 token.
 *
 * We crop-on-upload to 3:2 (via the shared `CoverCropper`) and display at
 * that same ratio on every surface so the framed image appears
 * pixel-identical everywhere (client-crop-authoritative, mirroring the
 * shipped OKS-697 mission / OKS-768 masterclass / OKS-855 brand patterns).
 * The ratio lives HERE, once, so a future ratio change is a single edit —
 * retarget these two constants + the model's
 * `MasterclassModule::COVER_CARD_VARIANT` and every surface follows.
 *
 * Two shapes are exported because two consumers need different forms:
 *
 *   - `MODULE_COVER_ASPECT_CLASS` — the Tailwind utility class string
 *     consumed by every card/preview display container (`cn(…CLASS, …)`).
 *     It MUST remain a literal `"aspect-[3/2]"` string in THIS file:
 *     Tailwind v4's JIT scans source for arbitrary-value utilities, so the
 *     literal has to exist somewhere it can see it. Keeping it here (and
 *     only here) is what makes the "one edit point" guarantee real —
 *     surfaces import the symbol rather than re-typing the class, so none
 *     can silently drift. LQE's Vitest parity test imports this exact
 *     symbol and scans module surfaces for zero 16:5 imports.
 *
 *   - `MODULE_COVER_ASPECT_RATIO` — the numeric ratio (width / height) fed
 *     to `react-easy-crop`'s `aspect` prop and the crop-area math via the
 *     shared `CoverCropper`. Kept in lockstep with the class + the server
 *     variant geometry (`MasterclassModule::COVER_CARD_VARIANT = [1200,
 *     800]` → 1200 / 800 = 1.5).
 *
 * NOTE — card-cover only. This ratio governs the `:card` cover band
 * (module card, form preview). The creator Learn list uses the square
 * `:thumb` variant ([112, 112]), an intrinsic center-square derivation of
 * the card crop — it cannot be 3:2 and square, so it is deliberately
 * EXCLUDED from "framed==shown".
 *
 * @see app/models/masterclass_module.rb `COVER_CARD_VARIANT`
 */

/**
 * Tailwind aspect-ratio utility for every module-cover card/preview
 * container. Literal string (see module note) — do not build it dynamically.
 */
export const MODULE_COVER_ASPECT_CLASS = "aspect-[3/2]"

/**
 * Numeric module-cover ratio (width / height = 3 / 2 = 1.5). Feeds the
 * shared `CoverCropper` `react-easy-crop` `aspect` and the canvas crop-area
 * math. Mirrors the server `:card` variant geometry (`[1200, 800]`).
 */
export const MODULE_COVER_ASPECT_RATIO = 3 / 2
